import { useState } from "react"
import { useApolloClient } from "@apollo/client"
import { toast } from "@/v2-ui/toast"
import userQuery from "@/v2-backoffice-client/user/user.query.gql"
import type {
  UserQuery,
  UserQueryVariables,
} from "@/v2-backoffice-client/user/__types__/user.query"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { canSendActivation } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import {
  isCompleteEmail,
  normalizeEmail,
} from "@/v2-backoffice-client/crm/crm.utils"

// Filter columns of backoffice.Get_Acc_Users (user.api.go registry).
const EMAIL_COLUMN = "Email"
const IS_DELETED_COLUMN = "IsDeleted"

/**
 * The send-time check behind the invite and create dialogs (FUS-927): which
 * of these addresses already belong to a live Placepoint user who has NOT
 * activated Fusion login yet (Not migrated, Pending or Expired). Those are
 * the people an invitation or a direct create is wrong for — the account
 * exists, so "create your account" dead-ends on "a user with this ID
 * already exists" (the 2026-10-28 support case) — and Send activation is
 * right. Users already on Fusion login are not reported: an invitation to
 * them is a plain add-to-team they redeem by signing in.
 *
 * One batched list query against Backoffice (not the typing-time CRM
 * check, which is debounced and may still be in flight when the operator
 * clicks Send); the full user rows come back so the Migration Center can
 * open on them directly. A failed lookup never blocks the send: it toasts
 * and reports nobody.
 */
export function useUnmigratedUsersByEmails() {
  const client = useApolloClient()

  return async function findUnmigratedUsers(
    emails: string[],
  ): Promise<UserFragment[]> {
    const distinct = [
      ...new Set(emails.map(normalizeEmail).filter(isCompleteEmail)),
    ]
    if (distinct.length === 0) return []

    try {
      const result = await client.query<UserQuery, UserQueryVariables>({
        query: userQuery,
        fetchPolicy: "network-only",
        variables: {
          input: {
            // Several live rows can share an address (staff double-rows);
            // leave room so none is cut off the page.
            limit: distinct.length * 5,
            filters: [
              { column: EMAIL_COLUMN, in: distinct },
              { column: IS_DELETED_COLUMN, eq: "0" },
            ],
          },
        },
      })
      // errorPolicy is "all" app-wide: a rejected query resolves with
      // errors (and no list) instead of throwing.
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data.getUsers.items.filter(
        (user) => !user.IsDeleted && canSendActivation(user.MigrationStatus),
      )
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn't check for existing accounts",
        description: `${error instanceof Error ? error.message : "Unknown error"}. Continuing without the check.`,
      })
      return []
    }
  }
}

type UserMigratePrompt<TData> = {
  /** The existing, not-yet-activated users found among the batch's emails. */
  users: UserFragment[]
  /** The submission the operator was about to send, held for their decision. */
  data: TData
}

/**
 * Host-side state for the prompt: run `intercept` with the batch's emails
 * before sending; when it answers true the prompt has the submission and
 * the host stops — it continues from the prompt's callbacks (send anyway,
 * or send what's left after `withoutUsers`).
 */
export function useUserMigratePrompt<TData>() {
  const findUnmigratedUsers = useUnmigratedUsersByEmails()
  const [checking, setChecking] = useState(false)
  const [prompt, setPrompt] = useState<UserMigratePrompt<TData> | null>(null)

  async function intercept(emails: string[], data: TData): Promise<boolean> {
    setChecking(true)
    try {
      const users = await findUnmigratedUsers(emails)
      if (users.length === 0) return false
      setPrompt({ users, data })
      return true
    } finally {
      setChecking(false)
    }
  }

  function dismiss() {
    setPrompt(null)
  }

  return { checking, prompt, intercept, dismiss }
}

/** The batch rows that are NOT one of the prompted users — what a host still sends. */
export function withoutPromptedUsers<TRow extends { Email: string }>(
  rows: TRow[],
  users: UserFragment[],
): TRow[] {
  const promptedEmails = new Set(
    users.map((user) => normalizeEmail(user.Email ?? "")),
  )
  return rows.filter((row) => !promptedEmails.has(normalizeEmail(row.Email)))
}
